<template>
    <div>
        <c-modal
            size="lg"
            :title="$t('targets.bulkEditing')"
            :ok-title="$t('modal.save')"
            :cancelTitle="$t('modal.cancel')"
            :noCloseOnEsc="true"
            :noCloseOnBackdrop="true"
            @okHandler="updateInLargeScale"
            isModalRoute
        >
            <b-container fluid v-if="currentUserHasPermission">
                <b-row>
                    <b-col>
                        <h5>
                            <b-badge class="p-2" variant="primary">
                                {{
                                    $t('usersBulkEdit.messages.youAreUpdating', {
                                        users: goalTotal,
                                    })
                                }}
                            </b-badge>
                        </h5>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <div class="mb-2">
                            <small class="text-muted">
                                <i class="fa fa-angle-double-right fa-lg mr-2"></i>
                                <strong>
                                    {{ $t('targets.infoForUserBulkSelected') }}
                                </strong>
                            </small>
                        </div>
                    </b-col>
                </b-row>

                <b-form novalidate>
                    <b-row>
                        <b-col md="12">
                            <b-form-checkbox v-model="form.isOwnerChecked" name="check-button" switch class="mb-2">
                                <span>
                                    {{ $t('form.label.owner') }}
                                    <span v-if="form.isOwnerChecked">*</span>
                                </span>
                            </b-form-checkbox>
                            <div v-if="form.isOwnerChecked">
                                <c-users
                                    :value.sync="$v.form.owner.$model"
                                    :state="checkState('owner')"
                                    @ready="isOwnerReady = $event"
                                >
                                    <span v-if="form.isOwnerChecked && !$v.form.title.required">{{
                                        $t('form.validations.required')
                                    }}</span>
                                </c-users>
                            </div>
                        </b-col>

                        <b-col md="12">
                            <b-form-checkbox v-model="form.isTitleChecked" name="check-button" switch class="mb-2">
                                <span>
                                    {{ $t('form.label.title') }}
                                    <span v-if="form.isTitleChecked">*</span>
                                </span>
                            </b-form-checkbox>
                            <div v-if="form.isTitleChecked">
                                <c-title
                                    :state="checkState('title')"
                                    :placeholder="$t('form.placeholder.title')"
                                    :value.sync="$v.form.title.$model"
                                >
                                    <span v-if="form.isTitleChecked && !$v.form.title.required">{{
                                        $t('form.validations.required')
                                    }}</span>
                                </c-title>
                            </div>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col md="12">
                            <b-form-checkbox
                                v-model="form.isDescriptionChecked"
                                name="check-button"
                                switch
                                class="mb-2"
                            >
                                <span>{{ $t('form.label.description') }}</span>
                            </b-form-checkbox>

                            <div v-if="form.isDescriptionChecked">
                                <c-description
                                    :state="checkState('description')"
                                    :value.sync="$v.form.description.$model"
                                    :placeholder="$t('form.placeholder.targetDescription')"
                                />
                            </div>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col>
                            <b-form-checkbox v-model="form.isCheckinsChecked" name="check-button" switch class="mb-2">
                                <span>
                                    {{ $t('people.blockAndUnblockCheckins') }}
                                </span>
                            </b-form-checkbox>
                            <div v-if="form.isCheckinsChecked">
                                <b-row>
                                    <b-col cols="12">
                                        <div class="mb-2">
                                            <small class="text-muted">
                                                <i class="fa fa-angle-double-right fa-lg mr-2" />
                                                <strong>{{ $t('form.description.blockUnblockCheckins') }}</strong>
                                            </small>
                                        </div>
                                    </b-col>
                                    <b-col cols="12" lg="4">
                                        <div class="mb-2">
                                            <b-form-select
                                                v-model="$v.form.selectedOption.$model"
                                                :options="lockUnlockCheckinsOptions"
                                            />
                                        </div>
                                    </b-col>
                                    <b-col>
                                        <c-date-picker
                                            v-model="$v.form.checkinsDate.$model"
                                            is-range
                                            mode="date"
                                            show-caps
                                            nav-visibility="click"
                                            :popover="{ visibility: 'click' }"
                                        >
                                            <template v-slot="{ inputValue, inputEvents }">
                                                <b-input-group>
                                                    <template slot="prepend">
                                                        <b-input-group-text>
                                                            <i class="fa fa-calendar" />
                                                        </b-input-group-text>
                                                    </template>
                                                    <DateInput
                                                        :placeholder="$t('vueFormWizard.startDate')"
                                                        :value="form.checkinsDate.start"
                                                        :inputValue="inputValue.start"
                                                        :inputEvents="inputEvents.start"
                                                        :state="checkinsDateState"
                                                    />
                                                    <DateInput
                                                        :placeholder="$t('vueFormWizard.endDate')"
                                                        :value="form.checkinsDate.end"
                                                        :inputValue="inputValue.end"
                                                        :inputEvents="inputEvents.end"
                                                        :state="checkinsDateState"
                                                    />
                                                    <b-button variant="danger" size="md" @click="clearRange()">
                                                        <i class="fa fa-trash" />
                                                    </b-button>
                                                    <b-form-invalid-feedback id="input-date-lock-unlock-checkins">
                                                        <span>
                                                            {{ $t('form.validations.required') }}
                                                        </span>
                                                    </b-form-invalid-feedback>
                                                </b-input-group>
                                            </template>
                                        </c-date-picker>
                                    </b-col>
                                </b-row>
                            </div>
                        </b-col>
                    </b-row>
                </b-form>
            </b-container>
            <b-container v-else>
                <b-alert show variant="danger">
                    {{ $t('usersBulkEdit.messages.userNotAllowed') }}
                </b-alert>
            </b-container>
        </c-modal>
    </div>
</template>

<script>
import i18n from '@/i18n'
import api from '@/services/api'
import Swal from 'sweetalert2'
import CModal from '@/views/components/modal'
import CLoader from '@/views/components/loader'
import {
    Title as CTitle,
    Owner as COwner,
    Description as CDescription,
    Users as CUsers,
} from '@/views/components/fields'
import { EventBuzz } from '@/main'
import { validationMixin } from 'vuelidate'
import { requiredIf } from 'vuelidate/lib/validators'
import { setupCalendar, DatePicker as CDatePicker } from 'v-calendar'
import { getFirstDayOfWeek, formatDate } from '@/lib/date'
import defaultOptions from '@/data/lock-unlock-checkins'
import { getUserPermissions } from '@/services/user-permissions'
import { DateInput } from '@/views/components/date'

setupCalendar({
    locale: i18n.locale,
    navVisibility: 'focus',
    popoverDirection: 'top',
    popoverVisibility: 'focus',
    firstDayOfWeek: getFirstDayOfWeek(i18n.locale),
})

export default {
    name: 'BulkEditing',
    data() {
        return {
            currentUserHasPermission: null,
            form: {
                title: null,
                owner: null,
                description: null,
                checkinsDate: {
                    start: null,
                    end: null,
                },
                isOwnerChecked: false,
                isTitleChecked: false,
                isDescriptionChecked: false,
                isCheckinsChecked: false,
                selectedOption: true,
            },
            lockUnlockCheckinsOptions: defaultOptions,
            goalTotal: 0,
        }
    },
    mixins: [validationMixin],
    validations: {
        form: {
            isOwnerChecked: {},
            isTitleChecked: {},
            isDescriptionChecked: {},
            isCheckinsChecked: {},
            selectedOption: {},
            title: {
                required: requiredIf((nestedModel) => nestedModel.isTitleChecked),
            },
            owner: {
                required: requiredIf((nestedModel) => nestedModel.isOwnerChecked),
            },
            description: {},
            checkinsDate: {
                start: {
                    required: requiredIf(function () {
                        return this.$v.form.isCheckinsChecked.$model
                    }),
                },
                end: {
                    required: requiredIf(function () {
                        return this.$v.form.isCheckinsChecked.$model
                    }),
                },
            },
        },
    },
    components: {
        CTitle,
        COwner,
        CModal,
        CDescription,
        CLoader,
        CUsers,
        CDatePicker,
        DateInput,
    },
    computed: {
        isValid() {
            return !this.$v.form.$anyError
        },
        isDirty() {
            return this.$v.form.$anyDirty
        },
        checkinsDateState() {
            return this.$v.form.checkinsDate.start.$model || this.$v.form.checkinsDate.end.$model ? true : false
        },
    },
    mounted() {
        this.currentUserHasPermission =
            getUserPermissions().is_admin ||
            getUserPermissions().is_god ||
            getUserPermissions().is_local_administrator ||
            getUserPermissions().is_target_master
    },
    methods: {
        updateInLargeScale(event, closeModal) {
            event.preventDefault()

            const condition =
                !this.form.isOwnerChecked &&
                !this.form.isTitleChecked &&
                !this.form.isDescriptionChecked &&
                !this.form.isCheckinsChecked

            if (condition) {
                Swal({
                    type: 'warning',
                    title: i18n.t('targets.changeGoalsInputWarning'),
                    cancelButtonText: i18n.t(`modal.close`),
                    showCancelButton: true,
                    showConfirmButton: false,
                })
                return
            }

            const validate = this.validate()

            if (!validate) {
                return
            }

            const { title, owner, description, checkinsDate, selectedOption } = this.form

            Swal.queue([
                {
                    type: 'warning',
                    title: i18n.t('notification.areYouSure'),
                    confirmButtonText: i18n.t('notification.yes'),
                    allowOutsideClick: false,
                    cancelButtonText: i18n.t('notification.no'),
                    showLoaderOnConfirm: true,
                    showCancelButton: true,
                    preConfirm: () => {
                        return api
                            .post(`goals/update-in-large-scale`, {
                                id: this.$store.state.goal.items.map((goal) => goal.id),
                                title,
                                owner: owner ? owner.value : null,
                                description,
                                checkinsState: checkinsDate
                                    ? {
                                          start_date: formatDate(checkinsDate.start, 'YYYY-MM-DD'),
                                          end_date: formatDate(checkinsDate.end, 'YYYY-MM-DD'),
                                          option: selectedOption,
                                      }
                                    : null,
                            })
                            .then((resp) => {
                                Swal.insertQueueStep({
                                    type: 'success',
                                    title: i18n.t('notification.requestSentSuccessfully'),
                                    text: i18n.t('notification.usersBulkEditRequestSuccessfullySent'),
                                })
                                closeModal()

                                EventBuzz.$emit('update-bulk-editing-targets', resp)

                                this.$store.dispatch('loadItems', [])
                            })
                            .catch((error) => {
                                Swal.insertQueueStep({
                                    title: i18n.t('notification.somethingWentWrong'),
                                    type: 'error',
                                    text: i18n.t('notification.usersBulkEditRequestFailed'),
                                })
                                console.log(error)
                            })
                    },
                },
            ])
        },
        checkState(val) {
            const field = this.$v.form[val]

            return !field.$dirty || !field.$invalid
        },
        validate() {
            this.$v.$touch()

            return this.isValid
        },
        touchFields(...fields) {
            fields.forEach((key) => {
                if (this.$v.form[key].$model) {
                    this.$v.form[key].$touch()
                }
            })
        },
        clearRange() {
            this.form.checkinsDate = {
                start: null,
                end: null,
            }
        },
    },
    created: function () {
        this.goalTotal = this.$store.state.goal.items.length
    },
}
</script>
